// The university badge palette (Phase 5 PR 5.0; plan §7 row 10): each
// university's chip colours, a background and the text drawn on it. It is the
// one sanctioned set of literal colours in Helios data: it replaced the 24
// legacy `--helios-surface-legacy-badge-*` tokens and the `.badge-*` rules in
// styles/global.css, retired in Phase 5 PR 5.10. lint:colors
// scans stylesheets only; these values reach the page as two component-internal
// custom properties set inline by UniversityBadge, never as `--helios-*`.
//
// A copy of the redesign's map (the retired campus-redesign-v1 experiment
// (removed in Phase 7), its 85 entries in the donor's order and spelling):
// Helios never imports the redesign tree (B1). The entries after the copy are
// Helios's own; each names its source. The pairs are palette colours: they
// keep their value in both themes. Pairs below 4.5:1 are held by the
// university-badge test as a known list (item O140), never fixed silently.

export interface UniversityPalette {
  /** The chip's fill. */
  readonly background: string
  /** The name's colour on that fill. */
  readonly color: string
}

// Frozen two deep: the record and each pair (Object.freeze alone stops at the
// top level), so the data cannot be changed at runtime either.
function freezePairs(
  record: Record<string, UniversityPalette>,
): Readonly<Record<string, UniversityPalette>> {
  for (const pair of Object.values(record)) Object.freeze(pair)
  return Object.freeze(record)
}

export const UNIVERSITY_PALETTES: Readonly<Record<string, UniversityPalette>> =
  freezePairs({
    'Aquinas College': {background: '#003E70', color: '#FFFFFF'},
    'Babson College': {background: '#006450', color: '#FFFFFF'},
    'Brown University': {background: '#4E3629', color: '#FFFFFF'},
    'California State University – Northridge': {
      background: '#D22030',
      color: '#FFFFFF',
    },
    'California State University, Fullerton': {
      background: '#003366',
      color: '#FFFFFF',
    },
    'California State University, Sacramento': {
      background: '#00573F',
      color: '#FFFFFF',
    },
    'Campus — Sacramento': {background: '#4F50FF', color: '#FFFFFF'},
    'Carnegie Mellon University': {background: '#C41230', color: '#FFFFFF'},
    'Clemson University': {background: '#F66733', color: '#000000'},
    Columbia: {background: '#B9D9EB', color: '#000000'},
    'Columbia University': {background: '#B9D9EB', color: '#000000'},
    Cornell: {background: '#B31B1B', color: '#FFFFFF'},
    'De Anza College': {background: '#005B96', color: '#FFFFFF'},
    'Emory University': {background: '#0033A0', color: '#FFFFFF'},
    'Gonzaga University': {background: '#002967', color: '#FFFFFF'},
    'Harvard University': {background: '#A51C30', color: '#FFFFFF'},
    Howard: {background: '#003A63', color: '#FFFFFF'},
    'Indiana University of Pennsylvania': {
      background: '#7C2529',
      color: '#FFFFFF',
    },
    'Johns Hopkins University': {background: '#002D72', color: '#FFFFFF'},
    'Kaplan University': {background: '#0077C0', color: '#FFFFFF'},
    'Keller Graduate School of Management': {
      background: '#B31B1B',
      color: '#FFFFFF',
    },
    'Lincoln Law School': {background: '#263746', color: '#FFFFFF'},
    'Loyola Maryland': {background: '#006341', color: '#FFFFFF'},
    'Loyola University Maryland': {background: '#006341', color: '#FFFFFF'},
    MIT: {background: '#A31F34', color: '#FFFFFF'},
    Morehouse: {background: '#9E1B32', color: '#FFFFFF'},
    'Morehouse College': {background: '#9E1B32', color: '#FFFFFF'},
    "Mount Saint Mary's University": {background: '#1F3A5F', color: '#FFFFFF'},
    Northeastern: {background: '#CC0000', color: '#FFFFFF'},
    NYU: {background: '#57068C', color: '#FFFFFF'},
    'New York University': {background: '#57068C', color: '#FFFFFF'},
    Pepperdine: {background: '#F15A22', color: '#000000'},
    Princeton: {background: '#E77500', color: '#000000'},
    'Princeton University': {background: '#E77500', color: '#000000'},
    'Penn State University': {background: '#041E42', color: '#FFFFFF'},
    'Quantic School of Business': {background: '#1A1A1A', color: '#FFFFFF'},
    'Regent University': {background: '#003A70', color: '#FFFFFF'},
    'Rhodes College': {background: '#B01E23', color: '#FFFFFF'},
    'Ritsumeikan Asia Pacific University': {
      background: '#1C365B',
      color: '#FFFFFF',
    },
    Rutgers: {background: '#CC0033', color: '#FFFFFF'},
    'Rutgers University': {background: '#CC0033', color: '#FFFFFF'},
    'San Jose State University': {background: '#0055A2', color: '#FFFFFF'},
    'Sarah Lawrence College': {background: '#8A1538', color: '#FFFFFF'},
    'Seton Hall University Law School': {
      background: '#004B8D',
      color: '#FFFFFF',
    },
    'Southern Wesleyan University': {background: '#5B2C83', color: '#FFFFFF'},
    Spelman: {background: '#00573F', color: '#FFFFFF'},
    Stanford: {background: '#8C1515', color: '#FFFFFF'},
    'Texas A&M': {background: '#500000', color: '#FFFFFF'},
    'Texas A&M University': {background: '#500000', color: '#FFFFFF'},
    'Texas Tech University': {background: '#CC0000', color: '#FFFFFF'},
    UCLA: {background: '#2774AE', color: '#FFFFFF'},
    'UC Berkeley': {background: '#003262', color: '#FFFFFF'},
    'UC Davis': {background: '#022851', color: '#FFFFFF'},
    'UC Riverside': {background: '#003DA5', color: '#FFFFFF'},
    'UC Santa Barbara': {background: '#003660', color: '#FFFFFF'},
    'University of California, Berkeley': {
      background: '#003262',
      color: '#FFFFFF',
    },
    'University of California, Davis': {
      background: '#022851',
      color: '#FFFFFF',
    },
    'University of California, Irvine': {
      background: '#0064A4',
      color: '#FFFFFF',
    },
    'University of California, Los Angeles': {
      background: '#2774AE',
      color: '#FFFFFF',
    },
    'University of California, Riverside': {
      background: '#003DA5',
      color: '#FFFFFF',
    },
    'University of Florida': {background: '#FA4616', color: '#000000'},
    'University of Georgia': {background: '#BA0C2F', color: '#FFFFFF'},
    'University of Illinois Urbana-Champaign': {
      background: '#13294B',
      color: '#FFFFFF',
    },
    'University of Maryland': {background: '#E03A3E', color: '#FFFFFF'},
    'University of Maryland, College Park': {
      background: '#E03A3E',
      color: '#FFFFFF',
    },
    'University of Michigan': {background: '#FFCB05', color: '#000000'},
    'University of Minnesota-Twin Cities': {
      background: '#7A0019',
      color: '#FFFFFF',
    },
    'University of Missouri, Columbia': {
      background: '#F1B82D',
      color: '#000000',
    },
    'University of North Texas': {background: '#00853E', color: '#FFFFFF'},
    'University of Notre Dame': {background: '#0C2340', color: '#FFFFFF'},
    'University of Pennsylvania': {background: '#011F5B', color: '#FFFFFF'},
    'University of Phoenix': {background: '#F26822', color: '#000000'},
    'University of Pittsburgh': {background: '#003594', color: '#FFFFFF'},
    'University of South Florida': {background: '#006747', color: '#FFFFFF'},
    'University of Southern Queensland': {
      background: '#412252',
      color: '#FFFFFF',
    },
    'University of the Cumberlands': {background: '#1F4A36', color: '#FFFFFF'},
    'University of Texas at Austin': {background: '#BF5700', color: '#FFFFFF'},
    'University of Toledo': {background: '#003E7E', color: '#FFFFFF'},
    'University of Washington': {background: '#4B2E83', color: '#FFFFFF'},
    USC: {background: '#990000', color: '#FFFFFF'},
    'UT Austin': {background: '#BF5700', color: '#FFFFFF'},
    Vanderbilt: {background: '#CFAE70', color: '#000000'},
    'Vanderbilt University': {background: '#CFAE70', color: '#000000'},
    'Wilmington University': {background: '#003E70', color: '#FFFFFF'},
    'Xavier University of Louisiana': {background: '#004B8D', color: '#FFFFFF'},
    // ── Helios additions (not in the donor) ──────────────────────────────────
    // George Washington University: a professors.json university the donor
    // lacks; the colour of the paralegal page's legacy GWU tag (#033c5a; its
    // token left with that tag in PR 5.2) with white text.
    'George Washington University': {background: '#033C5A', color: '#FFFFFF'},
    // The paralegal page's tag text for the same university.
    GWU: {background: '#033C5A', color: '#FFFFFF'},
    // The legacy "Northwestern" chip (the IT and cybersecurity program pages,
    // Miller and Surtain Scholars): its legacy badge colour
    // (surface-legacy-badge-northwestern, #4e2a84), white text.
    Northwestern: {background: '#4E2A84', color: '#FFFFFF'},
    // The legacy "UMD" chip (try-college): the donor's University of Maryland
    // pair.
    UMD: {background: '#E03A3E', color: '#FFFFFF'},
    // The "St. John's" chip (the cybersecurity associate page's Ronald Dixon,
    // 2026-10-14): St. John's University red #CF102D from Design, white text
    // (5.6:1).
    "St. John's": {background: '#CF102D', color: '#FFFFFF'},
    // The "U.S. Air Force" chip (the cybersecurity lander's Donald Rarick,
    // 2026-10-15): #003594 from Design, white text (11.2:1).
    'U.S. Air Force': {background: '#003594', color: '#FFFFFF'},
  })

// Lookups go through maps, never the record itself, so a name such as
// "constructor" cannot reach an Object.prototype member. The folded map holds
// the names in lower case, so a chip whose source text is upper case
// ("UC BERKELEY", "VANDERBILT") finds its university. No two keys collide when
// folded (the university-badge test holds that).
const EXACT = new Map(Object.entries(UNIVERSITY_PALETTES))
const FOLDED = new Map(
  Object.entries(UNIVERSITY_PALETTES).map(([name, palette]) => [
    name.toLowerCase(),
    palette,
  ]),
)

/**
 * The palette for a name: the exact key first, then the key that matches it
 * ignoring case and surrounding space. `undefined` for a name the map does not
 * hold (the badge then renders the brand chip).
 */
export function getUniversityPalette(
  name: string,
): UniversityPalette | undefined {
  return EXACT.get(name) ?? FOLDED.get(name.trim().toLowerCase())
}
